import React, { useEffect, useState } from "react";
import api from "../api";
import { Save, CheckCircle2, XCircle, RefreshCw, Info } from "lucide-react";
import EmailTemplateBlock from "../components/EmailTemplateBlock";
import PasswordChange from "../components/PasswordChange";
import BackupRestore from "../components/BackupRestore";
import SettingsMirror from "../components/SettingsMirror";
import ResendDiagnostics from "../components/ResendDiagnostics";
import DataHealthAudit from "../components/DataHealthAudit";
import ReportV2Settings from "../components/ReportV2Settings";
import BrandingSettings from "../components/BrandingSettings";

const CHAIN_DEFAULTS = {
  speedRed: 70, speedYellow: 90,
  keywordsRed: 100, keywordsYellow: 300,
  authorityRed: 30, authorityYellow: 46,
  trafficRed: 100, trafficYellow: 500,
};

export default function Impostazioni() {
  const [s, setS] = useState(null);
  const [logs, setLogs] = useState({ recent: [], summary: [] });
  const [status, setStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [okMsg, setOkMsg] = useState("");

  // (Old inline preview / renderVars logic moved into <EmailTemplateBlock/>)

  const loadStatus = async () => {
    setStatusLoading(true);
    try { const { data } = await api.get("/integrations/status"); setStatus(data); }
    finally { setStatusLoading(false); }
  };

  useEffect(() => {
    api.get("/settings").then(r => setS(r.data));
    api.get("/api-logs").then(r => setLogs(r.data));
    loadStatus();
  }, []);

  if (!s) return <div><span className="spinner"/></div>;

  const upd = (k, v) => setS(prev => ({...prev, [k]: v}));

  const save = async () => {
    setSaving(true); setOkMsg("");
    try {
      await api.put("/settings", s);
      setOkMsg("Salvato!");
      setTimeout(()=>setOkMsg(""), 2000);
    } finally { setSaving(false); }
  };

  const StatusRow = ({ name, label, info }) => {
    const i = status?.[name];
    if (!i) return null;
    return (
      <div className="flex justify-between items-center" style={{padding:"12px 0",borderBottom:"1px solid var(--mv-border)"}} data-testid={`status-${name}`}>
        <div>
          <div style={{fontWeight:600}}>{label}</div>
          <div className="text-small text-muted">{i.message}{info && ` · ${info(i)}`}</div>
        </div>
        {i.ok
          ? <span className="badge badge-green"><CheckCircle2 size={12}/> Online</span>
          : i.configured ? <span className="badge badge-red"><XCircle size={12}/> Errore</span>
          : <span className="badge badge-gray">Non configurata</span>}
      </div>
    );
  };

  return (
    <div data-testid="settings-page">
      <div className="h-display mb-2">Impostazioni</div>
      <div className="text-muted mb-6">Stato integrazioni, soglie semafori, template email default e log API</div>

      <div className="card mb-4" data-testid="integrations-status-card">
        <div className="flex justify-between items-center mb-4">
          <div className="h-section">Stato integrazioni</div>
          <button onClick={loadStatus} className="btn btn-secondary btn-sm" disabled={statusLoading} data-testid="refresh-status-btn">
            {statusLoading ? <span className="spinner"/> : <><RefreshCw size={14}/> Ricontrolla</>}
          </button>
        </div>
        {status ? (
          <>
            <StatusRow name="openapi" label="OpenAPI.it (Company API)" info={(i)=>`host: ${i.host}`} />
            <StatusRow name="seozoom" label="SEOZoom" />
            <StatusRow name="dataforseo" label="DataForSEO (Lighthouse + Backlinks)" />
            <StatusRow name="resend" label="Resend Email" info={(i)=>`sender: ${i.sender}`} />
            {(status.openapi && !status.openapi.ok && status.openapi.configured) && (
              <div className="card mt-4" style={{background:"var(--mv-warning-bg)",borderLeft:"4px solid var(--mv-warning)"}}>
                <div style={{fontWeight:700,marginBottom:6}}>💡 Come abilitare OpenAPI.it (Company)</div>
                <ol className="text-small" style={{paddingLeft:18,margin:0,lineHeight:1.7}}>
                  <li>Vai su <a href="https://console.openapi.com/it/oauth" target="_blank" rel="noreferrer">console.openapi.com/it/oauth</a></li>
                  <li>Genera un nuovo token con scope: <code>IT-search</code>, <code>IT-marketing</code>, <code>IT-legalforms</code></li>
                  <li>Il vecchio servizio "Imprese" è <strong>deprecato dal 31/12/2025</strong> — devi usare la nuova "Company" API</li>
                  <li>Aggiorna <code>OPENAPI_IT_TOKEN</code> in <code>/app/backend/.env</code> e riavvia il backend</li>
                  <li>Per testare prima di acquistare crediti, imposta <code>OPENAPI_USE_SANDBOX=true</code></li>
                </ol>
              </div>
            )}
          </>
        ) : <div><span className="spinner"/></div>}
      </div>

      <div className="card mb-4" data-testid="chain-thresholds-card">
        <div className="h-section mb-2">Soglie della catena della visibilità (report)</div>
        <p className="text-small text-muted mb-4">
          Decidono quando ogni passaggio del report è verde, giallo o rosso. Le modifiche
          valgono subito anche sui report già inviati.
        </p>
        <div className="grid gap-4" style={{gridTemplateColumns:"1fr 1fr"}}>
          {[
            ["speedRed", "1 · Velocità mobile — rosso sotto", "speed-red"],
            ["speedYellow", "1 · Velocità mobile — verde da", "speed-yellow"],
            ["keywordsRed", "2 · Parole chiave — rosso sotto", "kw-red"],
            ["keywordsYellow", "2 · Parole chiave — verde da", "kw-yellow"],
            ["authorityRed", "3 · Autorevolezza — rosso sotto", "auth-red"],
            ["authorityYellow", "3 · Autorevolezza — verde da", "auth-yellow"],
            ["trafficRed", "Numeri laterali · Visite/mese — rosso sotto", "traffic-red"],
            ["trafficYellow", "Numeri laterali · Visite/mese — verde da", "traffic-yellow"],
          ].map(([key, label, tid]) => (
            <div key={key}>
              <label className="label">{label}</label>
              <input type="number" className="input" data-testid={`set-chain-${tid}`}
                value={(s.chainThresholds || CHAIN_DEFAULTS)[key] ?? CHAIN_DEFAULTS[key]}
                onChange={e=>upd("chainThresholds", {
                  ...CHAIN_DEFAULTS, ...(s.chainThresholds || {}), [key]: +e.target.value })}/>
            </div>
          ))}
        </div>
        <button className="btn btn-ghost btn-sm mt-3" data-testid="reset-chain-thresholds"
          onClick={()=>upd("chainThresholds", {...CHAIN_DEFAULTS})}>
          Ripristina i valori consigliati
        </button>
      </div>

      <div className="card mb-4">
        <div className="h-section mb-4">Soglie semaforo</div>
        <div className="grid gap-4" style={{gridTemplateColumns:"1fr 1fr 1fr"}}>
          <div>
            <label className="label">Traffico verde (&lt;)</label>
            <input type="number" className="input" value={s.trafficGreen} onChange={e=>upd("trafficGreen",+e.target.value)} data-testid="set-traffic-green"/>
          </div>
          <div>
            <label className="label">Traffico rosso (&gt;)</label>
            <input type="number" className="input" value={s.trafficRed} onChange={e=>upd("trafficRed",+e.target.value)} data-testid="set-traffic-red"/>
          </div>
          <div>
            <label className="label">Authority verde</label>
            <input type="number" className="input" value={s.authorityGreen} onChange={e=>upd("authorityGreen",+e.target.value)} data-testid="set-auth-green"/>
          </div>
          <div>
            <label className="label">Authority rosso</label>
            <input type="number" className="input" value={s.authorityRed} onChange={e=>upd("authorityRed",+e.target.value)} data-testid="set-auth-red"/>
          </div>
          <div>
            <label className="label">PageSpeed verde (&gt;)</label>
            <input type="number" className="input" value={s.pagespeedGreen} onChange={e=>upd("pagespeedGreen",+e.target.value)} data-testid="set-ps-green"/>
          </div>
          <div>
            <label className="label">PageSpeed rosso (&lt;)</label>
            <input type="number" className="input" value={s.pagespeedRed} onChange={e=>upd("pagespeedRed",+e.target.value)} data-testid="set-ps-red"/>
          </div>
          <div>
            <label className="label">Max analisi al giorno</label>
            <input type="number" className="input" value={s.maxAnalysesPerDay} onChange={e=>upd("maxAnalysesPerDay",+e.target.value)} data-testid="set-max-day"/>
          </div>
        </div>
      </div>

      <BrandingSettings/>

      <div className="card mb-4" data-testid="default-email-template-card">
        <div className="h-section mb-2">Template email default</div>

        <div className="card mb-3" style={{padding:14,background:"var(--mv-primary-bg,#fff5ed)",borderLeft:"4px solid var(--mv-primary)"}}>
          <div className="flex gap-2 items-start">
            <Info size={16} color="var(--mv-primary)" style={{marginTop:2,flexShrink:0}}/>
            <div className="text-small" style={{lineHeight:1.7}}>
              Questi sono i <strong>template DEFAULT</strong>: ogni nuova lista li eredita.
              Per <strong>verticalizzare il messaggio</strong> su una nicchia specifica (es. dentisti, avvocati, ristoratori)
              vai sulla <strong>Lista</strong> → <strong>"Modifica template email"</strong>.
              Le modifiche qui <strong>NON retroagiscono</strong> sulle liste già create.
            </div>
          </div>
        </div>

        <label className="label">Nome mittente default</label>
        <input className="input" value={s.defaultSenderName || ""}
          onChange={e=>upd("defaultSenderName", e.target.value)} data-testid="set-email-sender"/>

        <EmailTemplateBlock
          label="T1 · Primo invio (default)"
          subtitle="Email iniziale inviata manualmente o in bulk. Ogni nuova lista parte da questo template."
          testId="default-t1"
          subject={s.defaultEmailSubject || ""}
          html={s.defaultEmailBodyHtml || ""}
          onSubjectChange={(v)=>upd("defaultEmailSubject", v)}
          onHtmlChange={(v)=>upd("defaultEmailBodyHtml", v)}
          subjectPlaceholder="Es: Ho appena analizzato il sito {{DOMINIO}} (e c'è da preoccuparsi)"
          height={420}
        />

        <div className="card mt-3" style={{padding:12,background:"var(--mv-bg)"}}>
          <div className="text-small" style={{fontWeight:600,marginBottom:6}}>Segnaposti disponibili</div>
          <div className="text-small text-muted" style={{lineHeight:1.7}}>
            <code>{"{{SALUTO}}"}</code>, <code>{"{{TITOLARE}}"}</code>, <code>{"{{companyName}}"}</code>, <code>{"{{DOMINIO}}"}</code>,
            <code>{"{{CITTA}}"}</code>, <code>{"{{PAGESPEED_MOBILE}}"}</code>, <code>{"{{PAGESPEED_DESKTOP}}"}</code>,
            <code>{"{{SEOZOOM_AUTHORITY}}"}</code>, <code>{"{{SEOZOOM_TRAFFIC}}"}</code>, <code>{"{{TOTAL_KEYWORDS}}"}</code>,
            <code>{"{{analysisUrl}}"}</code>, <code>{"{{calendarUrl}}"}</code>, <code>{"{{bookUrl}}"}</code>
          </div>
        </div>
      </div>

      {/* ==================== FOLLOW-UP SEQUENCE ==================== */}
      <div className="card mb-4" data-testid="followup-settings-card">
        <div className="h-section mb-2">Sequenza di follow-up automatica</div>
        <div className="text-muted mb-4 text-small" style={{lineHeight:1.6}}>
          Dopo aver mandato la <strong>prima email (T1)</strong>, il sistema può inviare automaticamente
          un <strong>reminder (T2)</strong> dopo X giorni e un <strong>ultimo tentativo (T3)</strong>
          dopo Y giorni. La sequenza si <strong>interrompe automaticamente</strong> se il destinatario
          ha cliccato il link "Prenota la consulenza" (proxy di prenotazione: senza integrazione
          Google Calendar API non possiamo sapere se ha realmente fissato l'appuntamento).
        </div>

        <div className="card mb-3" style={{padding:12, background:"#fef3c7", borderLeft:"4px solid #f59e0b"}}>
          <div className="flex gap-2 items-start">
            <Info size={16} color="#92400e" style={{marginTop:2, flexShrink:0}}/>
            <div className="text-small" style={{lineHeight:1.6, color:"#78350f"}}>
              I template T2 e T3 si configurano <strong>per singola lista</strong> in
              "Modifica template email" (tab T2 / T3). Le impostazioni qui sotto sono le
              cadenze globali e i template di default che ogni nuova lista erediterà.
            </div>
          </div>
        </div>

        <label className="flex items-center gap-2" style={{cursor:"pointer", marginBottom:14}}>
          <input type="checkbox"
            checked={s.followupEnabled !== false}
            onChange={e=>upd("followupEnabled", e.target.checked)}
            data-testid="followup-enabled"/>
          <span style={{fontWeight:600}}>Abilita follow-up automatico</span>
        </label>

        <div className="grid gap-4" style={{gridTemplateColumns:"1fr 1fr"}}>
          <div>
            <label className="label">Giorni prima di inviare T2 (reminder)</label>
            <input type="number" min="1" max="60" className="input"
              value={s.followupT2Days ?? 5}
              onChange={e=>upd("followupT2Days", parseInt(e.target.value)||5)}
              data-testid="set-followup-t2-days"/>
          </div>
          <div>
            <label className="label">Giorni prima di inviare T3 (ultimo tentativo)</label>
            <input type="number" min="1" max="120" className="input"
              value={s.followupT3Days ?? 15}
              onChange={e=>upd("followupT3Days", parseInt(e.target.value)||15)}
              data-testid="set-followup-t3-days"/>
          </div>
        </div>

        <EmailTemplateBlock
          label="T2 · Reminder (default)"
          subtitle={`Inviato automaticamente ${s.followupT2Days ?? 5} giorni dopo T1, se il destinatario non ha cliccato il link "Prenota".`}
          testId="default-t2"
          subject={s.defaultEmailSubject2 || ""}
          html={s.defaultEmailBodyHtml2 || ""}
          onSubjectChange={(v)=>upd("defaultEmailSubject2", v)}
          onHtmlChange={(v)=>upd("defaultEmailBodyHtml2", v)}
          subjectPlaceholder="Es: Avevi tempo di vedere l'analisi del sito {{DOMINIO}}?"
          height={280}
        />

        <EmailTemplateBlock
          label="T3 · Ultimo tentativo (default)"
          subtitle={`Inviato automaticamente ${s.followupT3Days ?? 15} giorni dopo T1, ultimo messaggio della sequenza.`}
          testId="default-t3"
          subject={s.defaultEmailSubject3 || ""}
          html={s.defaultEmailBodyHtml3 || ""}
          onSubjectChange={(v)=>upd("defaultEmailSubject3", v)}
          onHtmlChange={(v)=>upd("defaultEmailBodyHtml3", v)}
          subjectPlaceholder="Es: Ultimo messaggio — chiudi tu o chiudo io?"
          height={280}
        />
      </div>

      {/* ==================== VIDEO TUTORIAL REPORT ==================== */}
      <div className="card mb-6" data-testid="settings-videos">
        <div className="h-section mb-2">Video tutorial del report</div>
        <div className="text-muted mb-3 text-small">
          URL dei video (YouTube, Loom, Vimeo) mostrati in cima ad ogni sezione del report pubblico.
          Lascia vuoto per mostrare il placeholder "Video tutorial in arrivo".
        </div>
        <div className="card mb-4" style={{padding:12,background:"var(--mv-primary-bg,#fff5ed)",borderLeft:"4px solid var(--mv-primary)"}}>
          <div className="flex gap-2 items-start">
            <Info size={16} color="var(--mv-primary)" style={{marginTop:2,flexShrink:0}}/>
            <div className="text-small" style={{lineHeight:1.6}}>
              <strong>Formati supportati:</strong> URL YouTube embed (<code>https://www.youtube.com/embed/XXX</code>),
              Loom embed (<code>https://www.loom.com/embed/XXX</code>) o Vimeo (<code>https://player.vimeo.com/video/XXX</code>).
              Per YouTube standard, copia l'URL del video dalla barra (es. <code>youtu.be/XXX</code> o <code>youtube.com/watch?v=XXX</code>):
              lo convertiamo automaticamente in embed.
            </div>
          </div>
        </div>
        {[
          {key:"panoramica", label:"Tab Panoramica"},
          {key:"pagespeed", label:"Tab PageSpeed"},
          {key:"seo", label:"Tab SEO & Traffico"},
          {key:"keywords", label:"Tab Parole Chiave"},
          {key:"consigli", label:"Tab Consigli"},
          {key:"glossario", label:"Tab Glossario"},
        ].map(v => (
          <div key={v.key} className="mb-3" data-testid={`video-${v.key}`}>
            <label className="label">{v.label}</label>
            <input className="input"
              placeholder="https://www.youtube.com/watch?v=… o https://www.loom.com/share/…"
              value={(s.reportVideos || {})[v.key] || ""}
              onChange={e=>upd("reportVideos", {...(s.reportVideos||{}), [v.key]: e.target.value})}
              data-testid={`video-input-${v.key}`}/>
          </div>
        ))}
      </div>

      {/* ==================== EMAIL INBOUND AUTO-RESPONSE ==================== */}
      <div className="card mb-6" data-testid="inbound-email-card">
        <div className="h-section mb-2">Email auto-response Lead Inbound</div>
        <div className="text-muted mb-3 text-small">
          Email inviata automaticamente a chi richiede un'analisi gratuita dai form su <strong>maxvalle.it</strong> /
          <strong> sitidaincubo.it</strong> (o da una landing Google/Facebook Ads). Diversa dal cold outreach.
        </div>
        <div className="card mb-3" style={{padding:12,background:"var(--mv-primary-bg,#fff5ed)",borderLeft:"4px solid var(--mv-primary)"}}>
          <div className="flex gap-2 items-start">
            <Info size={16} color="var(--mv-primary)" style={{marginTop:2,flexShrink:0}}/>
            <div className="text-small" style={{lineHeight:1.6}}>
              Segnaposti specifici inbound: <code>{"{{NOME_RICHIEDENTE}}"}</code>, <code>{"{{COGNOME_RICHIEDENTE}}"}</code>,
              <code>{"{{NOME_COMPLETO_RICHIEDENTE}}"}</code>, <code>{"{{AZIENDA_RICHIEDENTE}}"}</code>.
              Funzionano anche quelli standard (<code>{"{{DOMINIO}}"}</code>, <code>{"{{analysisUrl}}"}</code>, <code>{"{{calendarUrl}}"}</code>, ecc.).
            </div>
          </div>
        </div>
        <EmailTemplateBlock
          label="Email di conferma inbound"
          subtitle="Inviata appena un lead richiede l'analisi gratuita. Diversa dal cold outreach."
          testId="inbound"
          subject={s.inboundEmailSubject || ""}
          html={s.inboundEmailBodyHtml || ""}
          onSubjectChange={(v)=>upd("inboundEmailSubject", v)}
          onHtmlChange={(v)=>upd("inboundEmailBodyHtml", v)}
          subjectPlaceholder="Es: Grazie {{NOME_RICHIEDENTE}}, ecco l'analisi del tuo sito"
          height={420}
        />
      </div>

      <div className="flex gap-3 items-center mb-6">
        <button onClick={save} disabled={saving} className="btn btn-primary" data-testid="save-settings-btn">
          {saving ? <span className="spinner"/> : <><Save size={16}/> Salva impostazioni</>}
        </button>
        {okMsg && <span className="badge badge-green">{okMsg}</span>}
      </div>

      {/* ==================== SECURITY & MAINTENANCE ==================== */}
      <div className="h-section mb-2" style={{marginTop:8}}>Sicurezza & manutenzione</div>
      <ResendDiagnostics/>
      <DataHealthAudit/>
      <ReportV2Settings/>
      <SettingsMirror/>
      <PasswordChange/>
      <BackupRestore/>

      <div className="card">
        <div className="h-section mb-4">Log API recenti</div>
        <div className="grid gap-3 mb-4" style={{gridTemplateColumns:"repeat(auto-fit, minmax(150px,1fr))"}}>
          {logs.summary.map(row => (
            <div key={row.provider} style={{padding:12,background:"var(--mv-bg)",borderRadius:8}}>
              <div className="stat-label">{row.provider}</div>
              <div className="stat-num">{row.count}</div>
              {row.failures > 0 && <div className="text-small" style={{color:"var(--mv-danger)"}}>{row.failures} errori</div>}
            </div>
          ))}
        </div>
        {logs.recent.length > 0 && (
          <table className="tbl" style={{fontSize:12}}>
            <thead><tr><th>Provider</th><th>Endpoint</th><th>Successo</th><th>Quando</th></tr></thead>
            <tbody>
              {logs.recent.slice(0,15).map(l => (
                <tr key={l.id}>
                  <td>{l.provider}</td><td className="text-small">{l.endpoint}</td>
                  <td>{l.success ? "✓" : "✗"}</td>
                  <td className="text-small text-muted">{new Date(l.createdAt).toLocaleString("it-IT")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

